/**
 * @file CloseIndexingDialog.tsx
 * @description Окно «Закрыть сайт от поисковиков?» (phase19.16, партия Р3в,
 * v0.19.64; SB145, SB45): последствия списком и поле, куда нужно ввести
 * слово «закрыть», — кнопка активна только при точном слове (регистр и
 * пробелы по краям не важны). Сервер проверяет слово ещё раз
 * (`confirm: "закрыть"`). До sm — на весь экран с закреплённым низом.
 */

import { useEffect, useId, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { CLOSE_INDEXING_WORD, isCloseWord } from "../../../lib/site-seo/close-word";

const CONSEQUENCES = [
  "Яндекс и Google перестанут показывать сайт в поиске — обычно за несколько дней.",
  "Вернуть позиции быстро не получится: после открытия поисковики перечитывают сайт неделями.",
  "Покупатели по прямым ссылкам, из рекламы и соцсетей сайт по-прежнему видят.",
  "Пока сайт закрыт, во всём кабинете висит красная плашка.",
] as const;

interface Props {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function CloseIndexingDialog({ open, busy, onCancel, onConfirm }: Props) {
  const inputId = useId();
  const [word, setWord] = useState("");
  useEffect(() => {
    if (open) setWord("");
  }, [open]);
  const ready = isCloseWord(word);
  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !busy ? onCancel() : null)}>
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[35rem] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Закрыть сайт от поисковиков?</DialogTitle>
          <DialogDescription className="sr-only">Сайт пропадёт из поиска Яндекса и Google.</DialogDescription>
        </DialogHeader>
        <ul className="type-body flex list-disc flex-col gap-1 pl-5 text-on-surface">
          {CONSEQUENCES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <div className="flex flex-col gap-1">
          <Label htmlFor={inputId}>Чтобы подтвердить, введите слово «{CLOSE_INDEXING_WORD}»</Label>
          <Input id={inputId} value={word} autoComplete="off" disabled={busy} onChange={(e) => setWord(e.target.value)} />
        </div>
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 max-sm:mt-auto bg-surface-lowest px-6 py-3 [box-shadow:0_-1px_0_var(--outline-variant)] flex-row justify-end">
          <Button variant="ghost" className="max-sm:flex-1" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button variant="destructive-solid" className="max-sm:flex-1" disabled={!ready} loading={busy} onClick={onConfirm}>
            Закрыть от поисковиков
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
